import {
  ActionIcon,
  Alert,
  Button,
  Pagination,
  Skeleton,
  Switch,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowDown,
  IconArrowLeft,
  IconArrowsSort,
  IconArrowUp,
  IconDatabase,
  IconRefresh,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime } from '../../format.js';
import { PageHeader } from '../../PageHeader.js';
import { Panel } from '../../Panel.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { useShell } from '../../shell-context.js';
import { errorCode } from '../personnel/source.js';
import { instantDay } from './DocumentDetailsPanel.js';
import classes from './Documents.module.css';
import {
  formatStorageSize,
  revisionBare,
  revisionText,
  sectionHref,
  treeLabel,
} from './library.js';
import {
  type CellStatus,
  cellHasIssue,
  cellStatus,
  currentAcknowledgement,
  type DistributionSort,
  type DistributionSortColumn,
  distributionIssues,
  sortDistribution,
} from './office.js';
import type { DocumentSource } from './source.js';
import type {
  DistributionCell,
  DistributionRow,
  DistributionVessel,
  DistributionView,
  LibrarySection,
} from './types.js';
import { VesselFilesDialog } from './VesselFiles.js';

/** Sayfada satır sayısı (doküman). */
export const DISTRIBUTION_PAGE = 20;

const STATUS_TONE: Record<CellStatus, StatusTone> = {
  current: 'success',
  downloading: 'info',
  upcoming: 'info',
  stale: 'warning',
  deferred: 'warning',
  missing: 'danger',
  failed: 'danger',
  out_of_scope: 'neutral',
  no_current: 'neutral',
};

function SortHeader({
  column,
  label,
  sort,
  onSort,
  sticky = false,
}: {
  column: DistributionSortColumn;
  label: string;
  sort: DistributionSort;
  onSort: (sort: DistributionSort) => void;
  sticky?: boolean;
}) {
  const active = sort.column === column;
  let icon: ReactNode = <IconArrowsSort size={12} aria-hidden className={classes.sortIdle} />;
  if (active) {
    icon =
      sort.direction === 'asc' ? (
        <IconArrowUp size={12} aria-hidden />
      ) : (
        <IconArrowDown size={12} aria-hidden />
      );
  }
  return (
    <th
      scope="col"
      data-sticky={sticky || undefined}
      aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <UnstyledButton
        className={classes.sortButton}
        onClick={() =>
          onSort(
            active
              ? { column, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
              : { column, direction: column === 'issues' ? 'desc' : 'asc' },
          )
        }
      >
        {label}
        {icon}
      </UnstyledButton>
    </th>
  );
}

/**
 * Gemi başlığında dosya deposunun özeti (ADR 0028 #31 madde 6, #40 madde 7): kullanılan ve kota,
 * ertelenen ve başarısız dosyalar, disk düzeyi, gemide duraklatma.
 */
function VesselFilesSummary({ vessel }: { vessel: DistributionVessel }) {
  const { t, i18n } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.vesselFiles.${key}`, options ?? {});
  const files = vessel.files;
  if (!files) return null;
  const size = (bytes: number) => formatStorageSize(bytes, i18n.language);
  const notes = [
    files.deferredFiles > 0 ? k('deferred', { count: files.deferredFiles }) : null,
    files.failedFiles > 0 ? k('failed', { count: files.failedFiles }) : null,
    files.diskLevel !== 'ok' && files.diskLevel in DISK_KEYS ? k(`disk.${files.diskLevel}`) : null,
    files.pausedUntil ? k('paused') : null,
  ].filter((note): note is string => note !== null);
  return (
    <span className={classes.distFiles}>
      <span>{k('used', { used: size(files.usedBytes), quota: size(files.quotaBytes) })}</span>
      {notes.length > 0 ? <span className={classes.distFilesNote}>{notes.join(' · ')}</span> : null}
    </span>
  );
}

const DISK_KEYS = { low: true, critical: true } as const;

/** Gemi hücresi: gemideki revizyon, indirme durumu ve kaptanın teyidi (kim, ne zaman). */
function Cell({ cell, row }: { cell: DistributionCell; row: DistributionRow }) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.distribution.${key}`, options ?? {});
  const status = cellStatus(cell, row);
  // Yalnız yürürlükteki revizyonun teyidi "teyit edildi" sayılır (inceleme M8a3-R3)
  const ack = currentAcknowledgement(cell);
  const previous = ack ? null : cell.acknowledgement;
  const showAck = status === 'current' || status === 'stale' || status === 'upcoming';
  return (
    <td data-status={status} data-issue={cellHasIssue(cell, row) || undefined}>
      <div className={classes.distCell}>
        <span className={classes.distRevision}>
          {cell.receipt ? revisionText(cell.receipt.revisionNo) : '—'}
        </span>
        <StatusBadge tone={STATUS_TONE[status]}>{k(`status.${status}`)}</StatusBadge>
        {showAck ? (
          ack ? (
            <span className={classes.distAck} data-late={ack.late || undefined}>
              {k('acknowledged', { name: ack.byName ?? '—', date: instantDay(ack.at) })}
              {ack.late ? (
                <>
                  {' · '}
                  <strong>{k('late')}</strong>
                </>
              ) : null}
            </span>
          ) : (
            <span className={classes.distAck} data-missing>
              {k('notAcknowledged')}
              {previous
                ? ` · ${k('lastAcknowledged', { revision: revisionBare(previous.revisionNo) })}`
                : null}
            </span>
          )
        ) : null}
        {cell.withdrawal ? (
          <span className={classes.distAck}>
            {k('withdrawal', { revision: revisionBare(cell.withdrawal.revisionNo) })}
            {cell.withdrawal.acknowledgement
              ? ` · ${k('acknowledged', {
                  name: cell.withdrawal.acknowledgement.byName ?? '—',
                  date: instantDay(cell.withdrawal.acknowledgement.at),
                })}`
              : ` · ${k('notAcknowledged')}`}
          </span>
        ) : null}
        {cell.reads ? (
          <span className={classes.distAck}>
            {k('reads', { confirmed: cell.reads.confirmed, expected: cell.reads.expected })}
          </span>
        ) : null}
      </div>
    </td>
  );
}

export interface DistributionTableProps {
  view: DistributionView;
  sort: DistributionSort;
  onSort: (sort: DistributionSort) => void;
  page: number;
  onPage: (page: number) => void;
  onOpen: (documentId: string) => void;
  onlyIssues?: boolean;
  /** Geminin dosyaları: indirme ayarı ve USB için dışa aktarma (yalnız web ofis). */
  onVessel?: ((vessel: DistributionVessel) => void) | undefined;
}

/**
 * Dağıtım tablosu (plan M8a Task 17; ADR 0032 F6): satırlar doküman, sütunlar gemi. Eksik,
 * başarısız, eski revizyon ve geç teyit vurgulanır. Sıralanır ve sayfalanır; tablo kendi kabında
 * yatay kayar, ilk sütun sabittir (sayfa kaymaz).
 */
export function DistributionTable({
  view,
  sort,
  onSort,
  page,
  onPage,
  onOpen,
  onlyIssues = false,
  onVessel,
}: DistributionTableProps) {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.distribution.${key}`, options ?? {});
  const rows = useMemo(() => {
    const list = onlyIssues ? view.rows.filter((r) => distributionIssues(r) > 0) : view.rows;
    return sortDistribution(list, sort);
  }, [view, sort, onlyIssues]);
  const pages = Math.max(1, Math.ceil(rows.length / DISTRIBUTION_PAGE));
  const current = Math.min(page, pages);
  const visible = rows.slice((current - 1) * DISTRIBUTION_PAGE, current * DISTRIBUTION_PAGE);
  const from = rows.length === 0 ? 0 : (current - 1) * DISTRIBUTION_PAGE + 1;
  const to = Math.min(rows.length, current * DISTRIBUTION_PAGE);
  const section = t(`documents.sections.${view.section}.title`);

  if (view.vessels.length === 0) return <EmptyState title={k('noVessels')} />;
  if (rows.length === 0) return <EmptyState title={k('empty')} />;

  return (
    <>
      {/* Yatay kayma bu kapta; içindeki bağlantılarla klavyeyle gezilir */}
      <div className={classes.distWrap}>
        <table className={classes.distTable}>
          <caption className={classes.srOnly}>{k('caption', { section })}</caption>
          <thead>
            <tr>
              <SortHeader column="docNo" label={k('document')} sort={sort} onSort={onSort} sticky />
              <SortHeader column="issues" label={k('issues')} sort={sort} onSort={onSort} />
              {view.vessels.map((vessel) => (
                <th key={vessel.id} scope="col" className={classes.distVessel}>
                  <span className={classes.distVesselHead}>
                    <span className={classes.distVesselName} title={vessel.name}>
                      {vessel.name}
                    </span>
                    {onVessel ? (
                      <ActionIcon
                        variant="subtle"
                        size="sm"
                        aria-label={t('documents.vesselFiles.open', { vessel: vessel.name })}
                        onClick={() => onVessel(vessel)}
                      >
                        <IconDatabase size={15} />
                      </ActionIcon>
                    ) : null}
                  </span>
                  <span className={classes.distPacket}>
                    {vessel.lastPacketAt
                      ? k('lastPacket', { date: formatDateTime(vessel.lastPacketAt) })
                      : k('noPacket')}
                  </span>
                  <VesselFilesSummary vessel={vessel} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const issues = distributionIssues(row);
              return (
                <tr key={row.id}>
                  <th scope="row" data-sticky>
                    <UnstyledButton className={classes.distDoc} onClick={() => onOpen(row.id)}>
                      <span className={classes.distDocTitle} title={treeLabel(row)}>
                        {treeLabel(row)}
                      </span>
                      <span className={classes.distDocRev}>
                        {row.current ? revisionText(row.current.revisionNo) : '—'}
                      </span>
                    </UnstyledButton>
                  </th>
                  <td className={classes.numeric}>
                    {issues > 0 ? (
                      <Tooltip label={k('issues')} withArrow>
                        <span className={classes.distIssues}>
                          <IconAlertTriangle size={13} aria-hidden />
                          {issues}
                        </span>
                      </Tooltip>
                    ) : (
                      <span className={classes.muted}>0</span>
                    )}
                  </td>
                  {view.vessels.map((vessel) => {
                    const cell = row.cells.find((c) => c.vesselId === vessel.id);
                    return cell ? (
                      <Cell key={vessel.id} cell={cell} row={row} />
                    ) : (
                      <td key={vessel.id}>—</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={classes.distFooter}>
        <span className={classes.muted}>{k('showing', { from, to, total: rows.length })}</span>
        {pages > 1 ? (
          <Pagination
            size="sm"
            total={pages}
            value={current}
            onChange={onPage}
            getControlProps={(control) => ({ 'aria-label': t(`documents.page.${control}`) })}
          />
        ) : null}
      </div>
    </>
  );
}

/**
 * Compliance ▸ ISM Forms (ya da Manuals) ▸ Distribution (yalnız web ofis; menü öğesi değildir,
 * ADR 0019): başlıktaki "Distribution" düğmesiyle açılır.
 */
export function DistributionScreen({
  section,
  source,
}: {
  section: LibrarySection;
  source: DocumentSource;
}) {
  const { t } = useTranslation();
  const shell = useShell();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`documents.distribution.${key}`, options ?? {});
  const [view, setView] = useState<DistributionView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<DistributionSort>({ column: 'docNo', direction: 'asc' });
  const [page, setPage] = useState(1);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [vessel, setVessel] = useState<DistributionVessel | null>(null);
  const [canEditFiles, setCanEditFiles] = useState(false);
  const sectionTitle = t(`documents.sections.${section}.title`);

  useEffect(() => {
    let live = true;
    void source.permissions().then(
      (permissions) => live && setCanEditFiles(Boolean(permissions.fileSettings)),
      () => live && setCanEditFiles(false),
    );
    return () => {
      live = false;
    };
  }, [source]);

  const load = useCallback(async () => {
    if (!source.office) return;
    setError(null);
    try {
      setView(await source.office.distribution(section));
    } catch (failure) {
      setError(errorCode(failure));
    }
  }, [source, section]);

  useEffect(() => {
    setView(null);
    void load();
  }, [load]);

  let body: ReactNode;
  if (error) {
    body = (
      <EmptyState
        tone="danger"
        title={t('documents.screen.error')}
        description={t(`documents.errors.${error}`, { defaultValue: '' })}
        action={
          <Button
            size="xs"
            variant="light"
            leftSection={<IconRefresh size={14} />}
            onClick={() => void load()}
          >
            {t('documents.screen.retry')}
          </Button>
        }
      />
    );
  } else if (!view) {
    body = (
      <div aria-busy="true" className={classes.catSkeleton}>
        <span className={classes.srOnly}>{t('documents.screen.loading')}</span>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} height={34} />
        ))}
      </div>
    );
  } else {
    body = (
      <DistributionTable
        view={view}
        sort={sort}
        onSort={(next) => {
          setSort(next);
          setPage(1);
        }}
        page={page}
        onPage={setPage}
        onlyIssues={onlyIssues}
        onOpen={(id) => shell?.navigate(sectionHref(section, { doc: id }))}
        onVessel={source.office ? setVessel : undefined}
      />
    );
  }

  return (
    <div className={classes.distScreen}>
      <PageHeader
        title={`${sectionTitle} – ${k('title')}`}
        crumb={k('title')}
        description={k('subtitle')}
        actions={
          <Button
            variant="default"
            size="xs"
            leftSection={<IconArrowLeft size={14} />}
            onClick={() => shell?.navigate(sectionHref(section))}
          >
            {k('back', { section: sectionTitle })}
          </Button>
        }
      />
      {error && view ? (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} className={classes.notice}>
          {t(`documents.errors.${error}`, { defaultValue: t('documents.errors.GENERIC') })}
        </Alert>
      ) : null}
      <Panel
        title={k('caption', { section: sectionTitle })}
        className={classes.distPanel}
        extra={
          <Switch
            size="xs"
            label={k('onlyIssues')}
            checked={onlyIssues}
            onChange={(event) => {
              setOnlyIssues(event.currentTarget.checked);
              setPage(1);
            }}
          />
        }
      >
        {body}
      </Panel>
      {source.office ? (
        <VesselFilesDialog
          vessel={vessel}
          office={source.office}
          canEdit={canEditFiles}
          onClose={() => setVessel(null)}
          onSaved={() => void load()}
        />
      ) : null}
    </div>
  );
}
